import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { useDemoForm } from "@/context/DemoFormContext";
import { Sparkles, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemoFormModal = () => {
  const { t, lang } = useLang();
  const { open, initialPlan, closeForm } = useDemoForm();
  const f = t.demo_form;

  // website = campo trappola per i bot (nascosto: una persona non lo vede e lo lascia vuoto)
  const empty = { name: "", email: "", phone: "", salon: "", plan: "", message: "", consent: false, website: "" };
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ ...empty, plan: initialPlan || "" });
      setStatus("idle");
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPlan]);

  const handleChange = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setError(f.error);
      return;
    }
    setStatus("submitting");
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        salon: form.salon.trim() || null,
        plan: form.plan || null,
        message: form.message.trim() || null,
        locale: lang,
        website: form.website || null,
      };
      await axios.post(`${API}/leads`, payload);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.[0]?.msg || f.error);
    }
  };

  const plans = [
    { value: "", label: f.plan_any },
    { value: "essence", label: "Essence" },
    { value: "atelier", label: "Atelier" },
    { value: "maison", label: "Maison" },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeForm()}>
      <DialogContent
        data-testid="demo-form-modal"
        className="max-w-lg p-0 overflow-hidden bg-white border-black/10 rounded-3xl"
      >
        {status === "success" ? (
          <div className="p-10 text-center" data-testid="demo-form-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] flex items-center justify-center mb-5">
              <CheckCircle2 className="text-white" size={26} />
            </div>
            <h3 className="font-serif text-3xl text-[#1A1A1A] mb-3">{f.success_title}</h3>
            <p className="text-[#4A4A4A] font-light mb-7">{f.success_msg}</p>
            <button
              data-testid="demo-form-close-success"
              onClick={closeForm}
              className="inline-flex items-center bg-[#1A1A1A] text-white rounded-full px-6 py-3 text-sm tracking-wide hover:bg-[#333]"
            >
              {f.success_close}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-8 pt-8 pb-0 text-left">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">
                <Sparkles size={12} /> AURA
              </div>
              <DialogTitle className="font-serif font-light text-3xl text-[#1A1A1A] tracking-tight">
                {f.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#4A4A4A] font-light mt-1">
                {f.subtitle}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="px-8 pb-8 pt-6 space-y-4">
              <Field label={f.name}>
                <input
                  data-testid="demo-name"
                  maxLength={100}
                  required
                  minLength={2}
                  value={form.name}
                  onChange={handleChange("name")}
                  placeholder={f.name_ph}
                  className={inputCls}
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={f.email}>
                  <input
                    data-testid="demo-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder={f.email_ph}
                    className={inputCls}
                  />
                </Field>
                <Field label={f.phone}>
                  <input
                    data-testid="demo-phone"
                    maxLength={40}
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder={f.phone_ph}
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={f.salon}>
                  <input
                    data-testid="demo-salon"
                    maxLength={120}
                    value={form.salon}
                    onChange={handleChange("salon")}
                    placeholder={f.salon_ph}
                    className={inputCls}
                  />
                </Field>
                <Field label={f.plan}>
                  <select
                    data-testid="demo-plan"
                    value={form.plan}
                    onChange={handleChange("plan")}
                    className={inputCls}
                  >
                    {plans.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <input
                type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={form.website} onChange={handleChange("website")}
                style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              />
              <Field label={f.message}>
                <textarea
                  data-testid="demo-message"
                  rows={3}
                  maxLength={2000}
                  value={form.message}
                  onChange={handleChange("message")}
                  placeholder={f.message_ph}
                  className={`${inputCls} resize-none`}
                />
              </Field>

              {/* Informativa ex art. 13 GDPR: va data PRIMA della raccolta,
                  e il link deve puntare a un documento che esiste davvero.
                  La base giuridica qui è l'art. 6.1.b (misure precontrattuali
                  richieste dall'interessato), non il consenso: la spunta serve
                  a documentare la presa visione dell'informativa. */}
              <label className="flex items-start gap-3 text-xs text-[#4A4A4A] font-light cursor-pointer">
                <input
                  data-testid="demo-consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={handleChange("consent")}
                  className="mt-0.5 h-4 w-4 rounded border-black/20 accent-[#1A1A1A]"
                />
                {/* La spaziatura la porta la stringa tradotta: in italiano
                    "l'informativa" è elisa e non vuole spazio, in inglese
                    "the privacy notice" lo richiede. */}
                <span>
                  {f.consent}
                  <Link
                    to="/privacy"
                    target="_blank"
                    data-testid="demo-privacy-link"
                    onClick={(e) => e.stopPropagation()}
                    className="underline underline-offset-2 text-[#1A1A1A]"
                  >
                    {f.privacy_link}
                  </Link>
                  .
                </span>
              </label>
              <p className="text-[11px] text-[#737373] font-light leading-relaxed">
                {f.privacy_note}
              </p>

              {status === "error" && (
                <div data-testid="demo-form-error" className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-2xl px-4 py-2">
                  {error || f.error}
                </div>
              )}

              <button
                type="submit"
                data-testid="demo-submit"
                disabled={status === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-60 text-white rounded-full px-6 py-3.5 text-sm tracking-wide transition-all"
              >
                {status === "submitting" ? <Loader2 size={16} className="animate-spin" /> : null}
                {status === "submitting" ? f.submitting : f.submit}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const inputCls =
  "w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-sm text-[#1A1A1A] placeholder:text-[#A3A3A3] focus:outline-none focus:border-[#A78BFA] focus:bg-white transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">{label}</span>
    {children}
  </label>
);
